
import React, { useEffect, useMemo, useState } from "react";
import useStore from "../../../services/Inventory/useStore";
import NoIndex from "../../../components/NoIndex";
import { ToastContainer } from "react-toastify";

const Stores = () => {
  const { getStores, getStoreItems, updateItemStatus } = useStore();

  const [stores, setStores] = useState([]);
  const [activeStoreId, setActiveStoreId] = useState("");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState({});
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [actionStatus, setActionStatus] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await getStores();
      setStores(data);
      if (data.length) setActiveStoreId(data[0].fk_userid);
    })();
  }, []);

  useEffect(() => {
    if (!activeStoreId) return;

    (async () => {
      setLoading(true);
      const data = await getStoreItems(activeStoreId);
      setItems(data);
      setLoading(false);
    })();
  }, [activeStoreId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return items;

    return items
      .map((group) => {
        const filteredItems = (group.item_list || []).filter((item) => {
          const itemId = String(item.item_id || "").toLowerCase();

          return (
            String(group.message || "")
              .toLowerCase()
              .includes(q) ||
            String(item.item_name || "")
              .toLowerCase()
              .includes(q) ||
            itemId === q ||
            String(item.size || "")
              .toLowerCase()
              .includes(q)
          );
        });

        return { ...group, item_list: filteredItems };
      })
      .filter((group) => group.item_list.length > 0);
  }, [items, search]);

  const toggleItem = (id) => {
    setSelectedItems((p) => ({ ...p, [id]: !p[id] }));
  };

  const toggleGroup = (group) => {
    const allSelected = group.item_list.every((i) => selectedItems[i.item_id]);
    const updated = { ...selectedItems };
    group.item_list.forEach((i) => {
      updated[i.item_id] = !allSelected;
    });
    setSelectedItems(updated);
  };

  const selectedList = useMemo(() => {
    return filtered.flatMap((g) =>
      g.item_list.filter((i) => selectedItems[i.item_id]),
    );
  }, [filtered, selectedItems]);

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);

      await updateItemStatus({
        fk_userid: activeStoreId,
        status: actionStatus,
        itemlist: selectedList.map((i) => ({ itemid: i.item_id })),
      });

      setShowConfirm(false);
      setSelectedItems({});

      setLoading(true);
      const updatedData = await getStoreItems(activeStoreId);
      setItems(updatedData);
      setLoading(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitLoading(false);
    }
  };

  const activeStore = stores.find(
    (s) => String(s.fk_userid) === String(activeStoreId),
  );

  const hasSelectedItems = Object.values(selectedItems).some(Boolean);

  const handleRefresh = async () => {
    const storeData = await getStores();
    setStores(storeData);

    if (activeStoreId) {
      setLoading(true);
      const itemData = await getStoreItems(activeStoreId);
      setItems(itemData);
      setLoading(false);
    }
  };

  return (
    <>
    <ToastContainer/>
    
    <div className="p-6 min-h-screen">
      <NoIndex/>
      <div className="flex justify-between items-center mb-6 montserrat-font-1">
        <h1 className="text-xl font-semibold">Instamart Store Items</h1>

        <div className="flex gap-2 items-center">
          <input
            className="border px-3 py-2 rounded-md text-sm w-72"
            placeholder="Search group / item / item id / size..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <button
            onClick={handleRefresh}
            className="border px-3 py-2 rounded-md"
          >
            ⟳
          </button>

          <button
            disabled={!hasSelectedItems}
            onClick={() => {
              setActionStatus("Y");
              setShowConfirm(true);
            }}
            className="bg-green-600 text-white px-4 py-2 rounded-md disabled:opacity-50"
          >
            Set Active
          </button>

          <button
            disabled={!hasSelectedItems}
            onClick={() => {
              setActionStatus("N");
              setShowConfirm(true);
            }}
            className="bg-red-500 text-white px-4 py-2 rounded-md disabled:opacity-50"
          >
            Set Inactive
          </button>
        </div>
      </div>

      {/* STORE CARDS */}
      <div className="flex gap-4 mb-6 flex-wrap montserrat-font-1">
        {stores.map((s) => {
          const active = s.fk_userid === activeStoreId;
          return (
            <div
              key={s.fk_userid}
              onClick={() => setActiveStoreId(s.fk_userid)}
              className={`cursor-pointer border rounded-xl p-4 w-56 ${
                active ? "border-green-500 bg-green-50" : "bg-white"
              }`}
            >
              <div className="font-semibold">{s.organization_name}</div>
              <div className="text-xs text-gray-500 mt-1">
                Store ID: {s.fk_userid}
              </div>
              <div className="text-xs text-gray-500">
                Total Items: {s.total_items}
              </div>
            </div>
          );
        })}
      </div>

      {/* SELECTED STORE INFO */}
      {/* {activeStore && (
        <div className="bg-white border rounded-xl p-4 mb-5 flex justify-between text-sm montserrat-font-1">
          <div>
            <div className="text-gray-500 text-xs">Selected Store</div>
            <div className="font-semibold">{activeStore.organization_name}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">fk_userid</div>
            <div className="font-semibold">{activeStore.fk_userid}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">Total Items</div>
            <div className="font-semibold">{activeStore.total_items}</div>
          </div>
        </div>
      )} */}

      {/* TABLE */}
      {loading ? (
        <div className="text-center py-10">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <div className="text-base font-medium text-gray-500">
            No items found for this store
          </div>
        </div>
      ) : (
        filtered.map((group) => {
          const items = group.item_list || [];
          const selectedCount = items.filter(
            (i) => selectedItems[i.item_id],
          ).length;

          return (
            <div
              key={group.message}
              className="bg-white border rounded-xl mb-6 montserrat-font-1"
            >
              {/* GROUP HEADER */}
              <div className="flex justify-between items-center p-4 border-b">
                <div>
                  <h2 className="font-semibold">{group.message}</h2>
                  <p className="text-xs text-gray-500">
                    {items.length} items • {selectedCount} selected
                  </p>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={
                      items.length > 0 &&
                      items.every((i) => selectedItems[i.item_id])
                    }
                    onChange={() => toggleGroup(group)}
                  />
                  Select all
                </label>
              </div>

              {/* TABLE HEADER */}
              <div className="grid grid-cols-8 text-xs font-medium text-gray-500 bg-green-100 px-4 py-2 border-b montserrat-font-1">
                <div>Select</div>
                <div>Image</div>
                <div>Name</div>
                <div>Item ID</div>
                <div>Size</div>
                <div>Price</div>
                <div>Stock</div>
                <div>Status</div>
              </div>

              {/* ROWS */}
              <div className="max-h-[555px] overflow-y-auto">
              {items.map((item) => {
                const isActive = item.inStock === "Y";

                return (
                  <div
                    key={item.item_id}
                    className="grid grid-cols-8 items-start px-4 py-3 border-b text-sm montserrat-font-1"
                  >
                    {/* Select */}
                    <div className="pt-1">
                      <input
                        type="checkbox"
                        checked={!!selectedItems[item.item_id]}
                        onChange={() => toggleItem(item.item_id)}
                      />
                    </div>

                    {/* Image */}
                    <div>
                      {item.photo ? (
                        <img
                          src={item.photo}
                          className="w-10 h-10 rounded object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 flex items-center justify-center text-xs rounded">
                          No Img
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 pr-2">
                      <div className="font-medium break-words">
                        {item.item_name}
                      </div>
                      <div className="text-xs text-gray-400 break-words mt-0.5">
                        {item.item_description}
                      </div>
                    </div>

                    {/* Item ID */}
                    <div className="break-all">{item.item_id}</div>

                    {/* Size */}
                    <div>{item.size}</div>

                    {/* Price */}
                    <div>₹{item.price}</div>

                    {/* Stock */}
                    <div>{item.stock}</div>

                    {/* Status */}
                    <div>
                      <span
                        className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${
                          isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          );
        })
      )}

      {/* MODAL */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
          <div className="bg-white w-[420px] rounded-xl shadow-lg">
            {/* HEADER */}
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="font-semibold text-lg">
                {actionStatus === "Y"
                  ? "Confirm Set Active"
                  : "Confirm Set Inactive"}
              </h2>
              {!submitLoading && (
                <button
                  onClick={() => setShowConfirm(false)}
                  className="text-gray-400 hover:text-black text-xl"
                >
                  ✕
                </button>
              )}
            </div>

            {/* BODY */}
            <div className="p-4">
              <p className="text-sm mb-4">
                Are you sure you want to{" "}
                <span className="font-semibold">
                  {actionStatus === "Y" ? "set active" : "set inactive"}
                </span>{" "}
                these selected items for{" "}
                <span className="font-semibold">
                  {activeStore?.organization_name || "this store"}
                </span>
                ?
              </p>

              <div className="max-h-60 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                {selectedList.map((item) => (
                  <div
                    key={`${item.item_id}-${item.size_id || item.pk_item_id}`}
                    className="py-2 border-b last:border-none"
                  >
                    <div className="font-medium text-sm">{item.item_name}</div>
                    <div className="text-xs text-gray-500">
                      Item ID: {item.item_id}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FOOTER */}
            <div className="flex justify-end gap-2 p-4 border-t">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={submitLoading}
                className="px-4 py-2 border rounded-md text-sm disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                onClick={handleSubmit}
                disabled={submitLoading || !hasSelectedItems || !activeStoreId}
                className={`px-4 py-2 rounded-md text-sm text-white ${
                  actionStatus === "Y" ? "bg-green-600" : "bg-red-500"
                } disabled:opacity-50`}
              >
                {submitLoading
                  ? "Updating..."
                  : actionStatus === "Y"
                    ? "Confirm Active"
                    : "Confirm Inactive"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Stores;